import React, { useEffect, useState, useRef, MouseEvent } from 'react';
import { X, Calendar, ChevronLeft, ChevronRight, Play, Volume2, VolumeX, Sparkles } from 'lucide-react';
import { Memory, getImageStyleFromUrl } from '../types';

interface MemoryDetailModalProps {
  memory: Memory | null;
  onClose: () => void;
}

export default function MemoryDetailModal({ memory, onClose }: MemoryDetailModalProps) {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!memory) return;
    setActiveImageIndex(0);
    setIsPlaying(false);

    // Stop page scroll behind modal
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [memory]);

  if (!memory) return null;

  // Helper to safely parse media URLs list
  const getMediaUrls = (): string[] => {
    try {
      if (memory.mediaUrl && memory.mediaUrl.trim().startsWith('[')) {
        const parsed = JSON.parse(memory.mediaUrl);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {
      console.error('Failed to parse mediaUrl JSON:', e);
    }
    return [memory.mediaUrl];
  };

  const mediaUrls = getMediaUrls();

  const handlePrevImage = (e: MouseEvent) => {
    e.stopPropagation();
    setActiveImageIndex((prev) => (prev === 0 ? mediaUrls.length - 1 : prev - 1));
  };

  const handleNextImage = (e: MouseEvent) => {
    e.stopPropagation();
    setActiveImageIndex((prev) => (prev === mediaUrls.length - 1 ? 0 : prev + 1));
  };

  const togglePlay = () => {
    if (memory.mediaType !== 'video' || !videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    }
  };

  const toggleMute = (e: MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  const formattedDate = () => {
    if (!memory.date) return '';
    try {
      const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
      const dateObj = new Date(memory.date);
      return dateObj.toLocaleDateString('en-US', options);
    } catch {
      return memory.date;
    }
  };

  return (
    <div
      id="memory-detail-backdrop"
      className="fixed inset-0 z-50 bg-stone-950/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 overflow-y-auto animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-[#FDFCF9] text-[#1A241F] w-full max-w-4xl rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border border-[#E6E1D4] flex flex-col md:flex-row max-h-[90vh]">
        {/* Left Side: Media Carousel / Video */}
        <div className="relative w-full md:w-1/2 bg-[#0E1E16] flex items-center justify-center group h-[280px] sm:h-[360px] md:h-auto">
          {memory.mediaType === 'video' ? (
            <div className="relative w-full h-full min-h-[280px] flex items-center justify-center bg-black" onClick={togglePlay}>
              <video
                ref={videoRef}
                src={mediaUrls[0]}
                className="w-full h-full max-h-[50vh] md:max-h-[90vh] object-contain"
                loop
                muted={isMuted}
                playsInline
                preload="metadata"
              />
              {/* Play/Pause Overlay */}
              {!isPlaying && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/25 pointer-events-none transition-all duration-300">
                  <div className="bg-white/25 backdrop-blur-md p-4 rounded-full border border-white/30 text-white shadow-xl">
                    <Play size={32} fill="white" className="translate-x-0.5" />
                  </div>
                </div>
              )}
              {/* Mute/Unmute Icon */}
              <button
                onClick={toggleMute}
                className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-md hover:bg-black/80 text-white rounded-full p-2.5 transition-colors z-10 cursor-pointer border border-white/10"
              >
                {isMuted ? <VolumeX size={15} /> : <Volume2 size={15} />}
              </button>
            </div>
          ) : (
            <div className="relative w-full h-full flex items-center justify-center bg-black">
              <img
                src={mediaUrls[activeImageIndex]}
                alt={memory.title}
                referrerPolicy="no-referrer"
                className="w-full h-full max-h-[50vh] md:max-h-[90vh] object-contain"
                style={getImageStyleFromUrl(mediaUrls[activeImageIndex])}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />

              {/* Nav arrows (only if multiple images exist) */}
              {mediaUrls.length > 1 && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-3 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white border border-white/10 transition-all cursor-pointer"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-3 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white border border-white/10 transition-all cursor-pointer"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}

              {/* Quick indicator count */}
              <div className="absolute bottom-4 left-4 bg-black/60 border border-white/10 backdrop-blur-md px-3 py-1 rounded-full text-white text-xs">
                Photo {activeImageIndex + 1} of {mediaUrls.length}
              </div>
            </div>
          )}
        </div>

        {/* Right Side: Narrative Details */}
        <div className="w-full md:w-1/2 p-6 sm:p-8 flex flex-col justify-between overflow-y-auto max-h-[45vh] md:max-h-[90vh]">
          <div>
            {/* Header / Dismiss */}
            <div className="flex justify-between items-start mb-4">
              <span className="estate-chip">
                <Sparkles size={11} className="text-emerald-700" />
                Sanctuary Memory
              </span>
              <button
                onClick={onClose}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-[#65756C] hover:text-[#14532D] hover:bg-[#F0FDF4] rounded-full transition-colors cursor-pointer border border-[#E6E1D4]"
                title="Dismiss"
              >
                <span>Close</span>
                <X size={14} />
              </button>
            </div>

            {/* Memory Title */}
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#1A241F] leading-snug mb-3">
              {memory.title}
            </h2>

            {/* Memory Date badge */}
            {memory.date && (
              <div className="flex items-center gap-2 text-sm text-[#65756C] mb-6 font-medium">
                <Calendar size={15} className="text-[#14532D]" />
                <span>{formattedDate()}</span>
              </div>
            )}

            {/* Memory Description Narrative */}
            {memory.description && (
              <div className="text-[#37463E] text-base leading-relaxed mb-6">
                <div className="bg-[#FAF7F0] p-5 rounded-2xl border-l-4 border-[#14532D] shadow-xs">
                  <p className="font-serif italic text-base sm:text-lg text-[#24332B] leading-relaxed">
                    "{memory.description}"
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Sub-gallery thumbnail indicators */}
          {memory.mediaType !== 'video' && mediaUrls.length > 1 && (
            <div className="mt-6 border-t border-[#E6E1D4] pt-4">
              <span className="text-[10px] text-[#65756C] font-semibold uppercase tracking-wider block mb-2.5">
                All Photos in this Story
              </span>
              <div className="flex space-x-2 overflow-x-auto pb-1">
                {mediaUrls.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`relative w-14 h-14 rounded-xl overflow-hidden border-2 transition-all flex-shrink-0 cursor-pointer ${
                      activeImageIndex === idx 
                        ? 'border-[#14532D] ring-2 ring-emerald-500/30 scale-105' 
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={img} className="w-full h-full object-cover" style={getImageStyleFromUrl(img)} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
